import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { useActiveLocale } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

export default function Messages() {
  const { user } = useAuth();
  const { t } = useLang();
  const locale = useActiveLocale();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadThreads = () => api.threads().then(setThreads);
  useEffect(() => { loadThreads(); }, []);

  const openThread = async (th) => {
    setActive(th);
    const m = await api.thread(th.listing_id, th.other_user_id);
    setMsgs(m);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    await api.sendMessage({ listing_id: active.listing_id, recipient_id: active.other_user_id, content: text, locale });
    setText("");
    const m = await api.thread(active.listing_id, active.other_user_id);
    setMsgs(m);
    loadThreads();
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">{t.msg.title}</h1>
      <div className="mt-8 grid gap-6 rounded-lg border border-zinc-800 bg-zinc-950 md:grid-cols-[320px_1fr] md:divide-x md:divide-zinc-800">
        <div className="max-h-[70vh] overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">{t.msg.empty}</div>
          ) : threads.map((th) => (
            <button
              key={`${th.listing_id}_${th.other_user_id}`}
              onClick={() => openThread(th)}
              className={`flex w-full items-start gap-3 border-b border-zinc-800 p-4 text-left transition-colors hover:bg-zinc-900 ${active && active.listing_id === th.listing_id && active.other_user_id === th.other_user_id ? "bg-zinc-900" : ""}`}
              data-testid={`thread-${th.listing_id}`}
            >
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-red-600 text-sm font-bold">{th.other_user_name?.[0]?.toUpperCase() || "U"}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{th.other_user_name}</p>
                <p className="truncate text-xs text-zinc-500">{th.listing?.title}</p>
                <p className="mt-1 truncate text-xs text-zinc-400">{th.last_content}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="flex max-h-[70vh] flex-col">
          {!active ? (
            <div className="flex flex-1 items-center justify-center p-10 text-sm text-zinc-500">← {t.msg.empty}</div>
          ) : (
            <>
              <div className="border-b border-zinc-800 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{active.listing?.title}</p>
                <p className="mt-1 font-display text-lg text-white">{active.other_user_name}</p>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {msgs.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-4 py-2 text-sm ${mine ? "bg-red-600 text-white" : "bg-zinc-900 text-zinc-100"}`}>{m.content}</div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-zinc-800 p-3">
                <div className="flex gap-2">
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder={t.msg.placeholder} className="min-h-0 resize-none border-zinc-800 bg-black text-white" data-testid="msg-input" />
                  <Button onClick={send} className="bg-red-600 text-white hover:bg-red-500" data-testid="msg-send">{t.msg.send}</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
